import { useState, useCallback, useLayoutEffect } from 'react';
import ServiceHandler from '../../../../services/service-handler';
import {
    API_RESPONSE,
    ApiResponseStatus,
    LoadingStatus,
} from '../../../../contexts/ford-rides-context/ford-rides-context';
import {
    ImgSpcDealerModelDetailsApiDataTHPHVN,
    ImgSpcDealerModelDetailsApiDataZAF,
} from '../../../../models/serice-price-calculator-model';

export type DealerModelDetailsData =
    | ImgSpcDealerModelDetailsApiDataZAF
    | ImgSpcDealerModelDetailsApiDataTHPHVN
    | null;

export const useImgSpcDealerModelDetails = (
    current3LetterCountryCode: string
) => {
    const [dealerModelDetailsData, setDealerModelDetailsData] = useState<
        DealerModelDetailsData
    >(null);
    const [dealerModelDetailsLoading, setDealerModelDetailsLoading] = useState<
        LoadingStatus
    >(LoadingStatus.Ideal);
    const [
        dealerModelDetailsApiStatus,
        setDealerModelDetailsApiStatus,
    ] = useState<ApiResponseStatus>(API_RESPONSE.IDLE);

    const fetchDealerModelDetailResponse = useCallback(
        async (countryCode: string) => {
            setDealerModelDetailsLoading(LoadingStatus.InitialLoad);
            try {
                const dealerModelDetailsApiResponse = await ServiceHandler.ImgSpcServices.dealerModelDetails(
                    countryCode
                );

                if (
                    !dealerModelDetailsApiResponse.result ||
                    dealerModelDetailsApiResponse.message.toLocaleLowerCase() !==
                        'success'
                ) {
                    throw new Error('Failed to fetch dealer model details');
                } else if (
                    !dealerModelDetailsApiResponse.data ||
                    dealerModelDetailsApiResponse.data.dealersInfo.length ===
                        0 ||
                    dealerModelDetailsApiResponse.data.modelsInfo.length === 0
                ) {
                    throw new Error('No dealer/model details found');
                } else {
                    setDealerModelDetailsData(
                        dealerModelDetailsApiResponse.data
                    );
                    setDealerModelDetailsApiStatus(API_RESPONSE.SUCCESS);
                }
            } catch (error) {
                console.error(
                    'An error occurred while fetching dealer/model data:',
                    error
                );
                setDealerModelDetailsApiStatus(API_RESPONSE.FAILURE);
            } finally {
                setDealerModelDetailsLoading(LoadingStatus.LoadingComplete);
            }
        },
        []
    );

    useLayoutEffect(() => {
        if (current3LetterCountryCode) {
            fetchDealerModelDetailResponse(current3LetterCountryCode);
        }
    }, [current3LetterCountryCode, fetchDealerModelDetailResponse]);

    return {
        dealerModelDetailsData,
        dealerModelDetailsLoading,
        dealerModelDetailsApiStatus,
    };
};
